// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import config from "@/config";
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import usePreferredLocale from "@/hooks/usePreferredLocale";
import useQuery from "@/hooks/useQuery";
import { ARTWORK_CONTINUATION_POLICIES } from "@/shared/constants/homeCarousel";
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import Helmet from "react-helmet";
import Loader from "@/shared-components/loader/Loader";
import {
  buildLocalizedAlternateUrls,
  resolveCurrentPathLocale,
  resolveLocalizedCanonicalUrl,
} from "@/utils/localePublicUrl";

import HomeHeader from "../components/HomeHeader";

import VirtualList from "./components/VirtualList";

import "./index.css";
import fetchWrapper from "@/services/fetchWrapper.service";
import {
  resolveCategoryCollectionLabel,
  resolveCategoryQueryKey,
  resolvePublicCategoryType,
  safeDecodeRouteParam,
} from "@/utils/homeCategoryRoute";
import { isRestrictedArtwork } from "@/utils/artworkVisibility";

const SITE_URL = "https://suarte.art";

function readInitialCategorySeoPayload() {
  if (typeof window === "undefined") {
    return null;
  }

  const payload = window.__SUARTE_SSR__?.category;
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return payload.seoData && typeof payload.seoData === "object"
    ? payload.seoData
    : null;
}

function buildFallbackCanonicalUrl(pathname = "") {
  return `${SITE_URL}${pathname || ""}`;
}

function stripCategoryFilters(query = {}) {
  const nextQuery = { ...query };
  ["emotion", "feeling", "theme", "color", "medium", "price", "size"].forEach((key) => {
    delete nextQuery[key];
  });
  return nextQuery;
}

function reorderArtworksByPreferredIds(artworks = [], preferredIds = [])
{
  if (!Array.isArray(artworks) || artworks.length === 0) return [];
  if (!Array.isArray(preferredIds) || preferredIds.length === 0) return artworks;

  const normalizedPreferred = preferredIds
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  if (normalizedPreferred.length === 0) return artworks;

  const preferredIndex = new Map(
    normalizedPreferred.map((id, index) => [id, index])
  );

  const orderedHead = new Array(normalizedPreferred.length).fill(null);
  const tail = [];
  const consumed = new Set();

  artworks.forEach((artwork) => {
    const artworkId = String(artwork?._id || artwork?.id || "").trim();
    if (artworkId && preferredIndex.has(artworkId) && !consumed.has(artworkId)) {
      const index = preferredIndex.get(artworkId);
      orderedHead[index] = artwork;
      consumed.add(artworkId);
      return;
    }
    tail.push(artwork);
  });

  return [...orderedHead.filter(Boolean), ...tail];
}

function sanitizeCollectionQueryDataPayload(queryPayload) {
  if (!queryPayload?.data?.data || !Array.isArray(queryPayload.data.data)) {
    return queryPayload;
  }

  const filteredItems = queryPayload.data.data.filter((item) => (
    Boolean(item?.slug) && !isRestrictedArtwork(item)
  ));

  if (filteredItems.length === queryPayload.data.data.length) {
    return queryPayload;
  }

  return {
    ...queryPayload,
    data: {
      ...queryPayload.data,
      data: filteredItems,
      totalDocs: filteredItems.length,
    },
  };
}

export default function CollectionPage({ collectionEndpoint = null, collName = null, additionalParams = {}, title, metadescription }) {
  const pas = useParams();
  const { collection_id, search_type, search_item } = pas;
  const location = useLocation();
  const locale = usePreferredLocale();
  const internalSearchType = resolveCategoryQueryKey(search_type || "");
  const publicSearchType = resolvePublicCategoryType(search_type || "");
  const locationCollectionName = location?.state?.collectionName;
  const isHomeSliderCollectionRoute = location.pathname.startsWith("/collections/");
  const isCategoryCollection = Boolean(
    search_type &&
    search_item &&
    location.pathname.startsWith("/category/")
  );
  const preferredArtworkOrder = useMemo(
    () => (
      Array.isArray(location?.state?.preferredArtworkOrder)
        ? location.state.preferredArtworkOrder
        : []
    ),
    [location?.state?.preferredArtworkOrder]
  );
  const navigate = useNavigate();
  const resolvedCollectionEndpoint = collectionEndpoint || (isCategoryCollection ? "/search/artwork/" : `/category/${collection_id}`);
  const isInternalCollectionEndpoint = resolvedCollectionEndpoint.startsWith("/category/");
  const routeFilterValue = internalSearchType
    ? (additionalParams?.[internalSearchType] ?? safeDecodeRouteParam(search_item))
    : undefined;
  const [params, setParams] = useGetSearchParams({ validParams: ["sort"] });
  const [categorySeed] = useState(() => Math.floor(Math.random() * 1000000000));
  const [query, setQuery] = useState(() => {
    const baseQuery = {
      sort:
        ["theme", "emotion", "color", "medium", "price", "size"].indexOf(internalSearchType) !== -1 || isInternalCollectionEndpoint
          ? "Default"
          : "Relevant",
      q: "",
      ...(internalSearchType ? { [internalSearchType]: routeFilterValue } : {}),
      ...additionalParams,
    };

    if (isCategoryCollection) {
      return {
        ...baseQuery,
        sort: "Default",
        category_randomized: true,
        seed: categorySeed,
      };
    }

    return {
      ...baseQuery,
      ...params,
    };
  });

  const [limitedEditionData, setLimitedEditionData] = useState(null);
  const [collectionName, setCollectionName] = useState(locationCollectionName || "");
  const [categorySeo, setCategorySeo] = useState(() => readInitialCategorySeoPayload());
  const queryCacheKey = isCategoryCollection
    ? `${publicSearchType || internalSearchType || "category"}_${search_item || "all"}_collection_page`
    : `${collName || collection_id}_collection_page`;
  const { loading, queryData, loadMoreData } = useQuery(
    queryCacheKey,
    `${config.apis.api.url}${resolvedCollectionEndpoint}`,
    query,
    {
      injectToken: true,
    }
  );
  const [searchType, setSearchType] = useState(internalSearchType || search_type);
  const orderedQueryData = useMemo(() => {
    const sanitizedQueryData = sanitizeCollectionQueryDataPayload(queryData);
    const items = sanitizedQueryData?.data?.data;
    if (!sanitizedQueryData || !Array.isArray(items)) {
      return sanitizedQueryData;
    }
    if (!isInternalCollectionEndpoint || preferredArtworkOrder.length === 0) {
      return sanitizedQueryData;
    }
    const reorderedItems = reorderArtworksByPreferredIds(items, preferredArtworkOrder);
    if (reorderedItems === items) {
      return sanitizedQueryData;
    }
    return {
      ...sanitizedQueryData,
      data: {
        ...sanitizedQueryData.data,
        data: reorderedItems,
      },
    };
  }, [queryData, isInternalCollectionEndpoint, preferredArtworkOrder]);
  const capItem = collectionName
    ? collectionName.charAt(0).toUpperCase() + collectionName.slice(1)
    : "";
  const effectiveCategorySeo = isCategoryCollection ? categorySeo : null;
  const collectionTitle = effectiveCategorySeo?.title || title || `${collectionName} | Suarte`;
  const collectionDescription = effectiveCategorySeo?.metaDescription || metadescription || `A collection of ${collectionName} featured by Suarte`;
  const collectionRobots = effectiveCategorySeo?.robots || "index,follow,max-image-preview:large";
  const collectionCanonicalUrl = effectiveCategorySeo?.canonicalUrl || buildFallbackCanonicalUrl(location.pathname);
  const resolvedLocale = resolveCurrentPathLocale();
  const resolvedCanonicalUrl = resolveLocalizedCanonicalUrl({ canonicalUrl: collectionCanonicalUrl, locale: resolvedLocale });
  const alternateUrls = buildLocalizedAlternateUrls({ canonicalUrl: collectionCanonicalUrl, locale: resolvedLocale });
  const collectionOgImage = effectiveCategorySeo?.ogImage || "https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/public";
  const collectionStructuredData = effectiveCategorySeo?.structuredData || null;
  const shouldContinueToSuarteworld =
    resolvedCollectionEndpoint.startsWith("/category/")
    && !resolvedCollectionEndpoint.includes("/search/artwork");
  const navigateToArtwork = useNavigateToArtwork(
    `${collName || collection_id}_collection_page`,
    orderedQueryData?.data,
    `${resolvedCollectionEndpoint}${queryData?.queryString}`,
    {
      type: 'collection-page',
      label: capItem,
      continuation: shouldContinueToSuarteworld
        ? ARTWORK_CONTINUATION_POLICIES.SUARTEWORLD
        : undefined,
    }
  );

  function onSort(type) {
    if (isCategoryCollection) {
      return;
    }
    const newQuery = { ...query, sort: type };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  function onInput(value) {
    const newQuery = { ...query, q: value };
    setQuery(newQuery);
    if (!isCategoryCollection) {
      setParams(newQuery, { replace: true });
    }
  }

  useEffect(() => {
    if (additionalParams.price) {
      setSearchType("price");
    }
    if (additionalParams.size) {
      setSearchType("size");
    }
    if (additionalParams.emotion) {
      setSearchType("emotion");
    }
    if (additionalParams.medium) {
      setSearchType("medium");
    }
    if (additionalParams.theme) {
      setSearchType("theme");
    }
    if (additionalParams.color) {
      setSearchType("color");
    }
  }, [additionalParams, collectionName]);

  useEffect(() => {
    if (search_type) {
      setCollectionName(resolveCategoryCollectionLabel(search_type, search_item, locale));
      return;
    }
    if (collName) {
      setCollectionName(collName);
      return;
    }
    if (locationCollectionName) {
      setCollectionName(locationCollectionName);
      return;
    }
    if (!collection_id) return;
    const core = async () => {
      const { response, data } = await fetchWrapper.get(
        `${config.apis.api.url}/home/name/${collection_id}`,
        {
          injectToken: true,
        }
      );
      if (response.ok) {
        setCollectionName(data.name);
      }
    };
    core();
  }, [collection_id, collName, search_type, search_item, locationCollectionName, locale]);

  useEffect(() => {
    if (!isCategoryCollection) {
      return;
    }

    setQuery((currentQuery) => ({
      ...stripCategoryFilters(currentQuery),
      sort: "Default",
      category_randomized: true,
      seed: currentQuery.seed ?? categorySeed,
      ...(internalSearchType ? { [internalSearchType]: routeFilterValue } : {}),
    }));
  }, [isCategoryCollection, categorySeed, internalSearchType, routeFilterValue]);

  useEffect(() => {
    if (!isCategoryCollection) {
      setCategorySeo(null);
      return;
    }

    setCategorySeo(readInitialCategorySeoPayload());
  }, [isCategoryCollection, publicSearchType, search_item, locale]);

  useEffect(() => {
    if (!isCategoryCollection || !publicSearchType || !search_item) {
      return;
    }

    let cancelled = false;

    const core = async () => {
      const { response, data } = await fetchWrapper.get(
        `${config.apis.api.url}/seo/category/${encodeURIComponent(publicSearchType)}/${encodeURIComponent(search_item)}`,
        {
          injectToken: true,
        }
      );

      if (!cancelled && response?.ok && data && typeof data === "object") {
        setCategorySeo(data);
      } else if (!cancelled && response && !response.ok && data && typeof data === "object") {
        setCategorySeo(data);
      }
    };

    core();

    return () => {
      cancelled = true;
    };
  }, [isCategoryCollection, publicSearchType, search_item]);

  useEffect(() => {
    if (!isCategoryCollection && !collectionEndpoint && (!collection_id || collection_id.length === 0)) navigate("/");
    if (collName === "Limited Editions" && queryData) {
      let deepCopiedQueryData = _.cloneDeep(queryData);
      deepCopiedQueryData.data.data = deepCopiedQueryData.data.data.map((item) => {
        let newItem = { ...item };
        newItem.artwork_about.artwork_price = config.variables.LIMITED_EDITION_SMALL_PRICE;
        newItem.artwork_about.artwork_currency = "EUR";
        return newItem;
      });
      setLimitedEditionData(deepCopiedQueryData);
    }
  }, [queryData, pas, collectionEndpoint, collName, isCategoryCollection, collection_id, navigate]);

  return (
    <>
      <Helmet>
        <html lang={resolvedLocale} />
        <title>{collectionTitle}</title>
        <meta name="description" content={collectionDescription} />
        <meta name="robots" content={collectionRobots} />
        <meta name="googlebot" content={collectionRobots} />
        <link rel="canonical" href={resolvedCanonicalUrl} />
        {alternateUrls.map((alternate) => (
          <link key={alternate.hrefLang} rel="alternate" hrefLang={alternate.hrefLang} href={alternate.href} />
        ))}
        <meta property="og:type" content={effectiveCategorySeo?.ogType || "website"} />
        <meta property="og:title" content={collectionTitle} />
        <meta property="og:description" content={collectionDescription} />
        <meta property="og:url" content={resolvedCanonicalUrl} />
        <meta property="og:image" content={collectionOgImage} />
        <meta property="og:site_name" content="Suarte" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={collectionTitle} />
        <meta name="twitter:description" content={collectionDescription} />
        <meta name="twitter:image" content={collectionOgImage} />
        {collectionStructuredData ? (
          <script type="application/ld+json">
            {JSON.stringify(collectionStructuredData)}
          </script>
        ) : null}
      </Helmet>
      <AppNavigationPage>
        <div className="collection-page__page">
          <HomeHeader
            onInput={onInput}
            value={query.q}
            text={capItem}
            placeholder="Search for artworks..."
            type={searchType}
            onSort={onSort}
            defaultOption={query.sort}
            hideSearch={isHomeSliderCollectionRoute}
            hideSortControl={isCategoryCollection}
          />

          {loading ? (
            <Loader />
          ) : orderedQueryData?.data?.data?.length === 0 ? (
            <Text className="collection-page__empty-text font-size-medium" paragraph>
              {isCategoryCollection
                ? "No artworks available in this category."
                : "No artworks available for your search query."}
            </Text>
          ) : collectionName === "Limited Editions" ? (
            limitedEditionData && (
              <VirtualList
                key={limitedEditionData.queryString}
                items={limitedEditionData.data}
                onLoadMore={loadMoreData}
                navigateToArtwork={navigateToArtwork}
                collectionName={collectionName}
              />
            )
          ) : (
            <VirtualList
              key={orderedQueryData?.queryString}
              items={orderedQueryData?.data}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </div>
      </AppNavigationPage>
    </>
  );
}
